import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Search, Package, Eye, Pencil, Trash2, Archive, UserCheck, ArrowUp, ArrowDown, ArrowUpDown, Merge, MoreHorizontal, Download } from "lucide-react";
import { clientAPI, exportAPI } from "@/lib/api";
import { toast } from "sonner";
import { BulkActionBar, useSelection } from "@/components/BulkActionBar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ClientFormDialog } from "@/components/clients/ClientFormDialog";
import { DuplicatesDialog } from "@/components/clients/DuplicatesDialog";
import { InvoicedProductMatch, PRODUCT_MIN_CHARS, useDebouncedProductFilter } from "@/components/clients/InvoicedProductFilter";

const emptyForm = { type: "client", company: "", trade_name: "", first_name: "", last_name: "", email: "", phone: "", phones: [], emails: [], address: "", city: "", postal_code: "", country: "France", siret: "", rci: "", dsee: "", tva_number: "", notes: "" };

export default function ClientsPage({ user }) {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const urlType = searchParams.get("type") || "";
  const urlNew = searchParams.get("new");
  const [typeFilter, setTypeFilter] = useState(urlType);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState("company");
  const [sortOrder, setSortOrder] = useState("asc");
  const PAGE_SIZE = 100;
  const navigate = useNavigate();
  const [showDupes, setShowDupes] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [merging, setMerging] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const productFilter = useDebouncedProductFilter(productQuery);

  useEffect(() => { setTypeFilter(urlType); }, [urlType]);
  useEffect(() => { if (urlNew === "1") { setEditing(null); setForm({ ...emptyForm }); setShowDialog(true); } }, [urlNew]);

  // Ref for filter state to keep load stable
  const filtersRef = useRef({});
  filtersRef.current = { search, typeFilter, statusFilter, productFilter, page, sortField, sortOrder };

  const load = useCallback(() => {
    const f = filtersRef.current;
    const params = { limit: PAGE_SIZE, skip: (f.page - 1) * PAGE_SIZE, sort: f.sortField, order: f.sortOrder };
    if (f.search) params.search = f.search;
    if (f.typeFilter) params.type = f.typeFilter;
    if (f.statusFilter) params.status = f.statusFilter;
    if (f.productFilter) params.invoiced_product = f.productFilter;
    clientAPI.list(params).then(r => {
      setClients(r.data.items || r.data);
      setTotal(r.data.total || 0);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [search, typeFilter, statusFilter, productFilter, page, sortField, sortOrder, load]);

  // Reset page when filters/sort change
  useEffect(() => { setPage(1); }, [search, typeFilter, statusFilter, productFilter, sortField, sortOrder]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={12} className="ml-1 text-slate-300" />;
    return sortOrder === "asc" ? <ArrowUp size={12} className="ml-1 text-indigo-600" /> : <ArrowDown size={12} className="ml-1 text-indigo-600" />;
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm }); setShowDialog(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setShowDialog(true); };

  const save = async () => {
    if (!form.first_name.trim() && !form.company.trim()) { toast.error("Nom ou société requis"); return; }
    try {
      if (editing) { await clientAPI.update(editing.id, form); toast.success("Client modifié"); }
      else { await clientAPI.create(form); toast.success("Client créé"); }
      setShowDialog(false); load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce client ?")) return;
    await clientAPI.delete(id); toast.success("Supprimé"); load();
  };

  const f = (k, v) => setForm({ ...form, [k]: v });

  const loadDupes = async () => {
    try {
      const res = await clientAPI.duplicates();
      setDupes(res.data);
      setShowDupes(true);
    } catch { toast.error("Erreur chargement doublons"); }
  };

  const mergeGroup = async (group) => {
    if (!window.confirm(`Fusionner ${group.count} fiches "${group.company}" en une seule ?`)) return;
    setMerging(true);
    try {
      const keepId = group.clients[0].id;
      const mergeIds = group.clients.slice(1).map(c => c.id);
      const res = await clientAPI.merge(keepId, mergeIds);
      toast.success(`${res.data.merged} fiche(s) fusionnee(s) - ${res.data.phones.length} tel, ${res.data.emails.length} email(s)`);
      const updated = dupes.filter(d => d.company !== group.company);
      setDupes(updated);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur fusion"); }
    finally { setMerging(false); }
  };

  const mergeAll = async () => {
    if (!window.confirm(`Fusionner automatiquement les ${dupes.length} groupes de doublons ?`)) return;
    setMerging(true);
    let merged = 0;
    for (const group of dupes) {
      try {
        const keepId = group.clients[0].id;
        const mergeIds = group.clients.slice(1).map(c => c.id);
        await clientAPI.merge(keepId, mergeIds);
        merged++;
      } catch (e) { toast.error("Erreur fusion client"); }
    }
    toast.success(`${merged} groupe(s) fusionnes`);
    setDupes([]);
    setShowDupes(false);
    load();
    setMerging(false);
  };

  const selection = useSelection(clients);
  const [bulkLoading, setBulkLoading] = useState(false);

  const handleBulkAction = async (actionKey) => {
    if (!selection.selectedIds.length) return;
    if (actionKey === "delete" && !window.confirm(`Supprimer ${selection.count} contact(s) ?`)) return;
    setBulkLoading(true);
    try {
      const res = await clientAPI.bulkAction(selection.selectedIds, actionKey);
      toast.success(res.data.message);
      selection.clear();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur action groupée"); }
    finally { setBulkLoading(false); }
  };

  const clientBulkActions = [
    { key: "delete", label: "Supprimer", icon: Trash2, variant: "outline", className: "text-red-600 border-red-200 hover:bg-red-50" },
    ...(statusFilter === "archived"
      ? [{ key: "unarchive", label: "Desarchiver", icon: Archive, variant: "outline", className: "text-emerald-600 border-emerald-200 hover:bg-emerald-50" }]
      : [{ key: "archive", label: "Archiver", icon: Archive, variant: "outline", className: "text-orange-600 border-orange-200 hover:bg-orange-50" }]),
    ...(clients.some(c => selection.selected.has(c.id) && c.type === "prospect") ? [{ key: "convert", label: "Convertir en client", icon: UserCheck, variant: "outline", className: "text-emerald-600 border-emerald-200 hover:bg-emerald-50" }] : []),
  ];

  return (
    <div className="page-container" data-testid="clients-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Clients & Prospects</h1>
        <p className="text-slate-500 mt-1">Gérez votre portefeuille clients</p>
      </div>
      <Card className="p-6 border border-slate-200 rounded-lg bg-white">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Société, nom, email, tel..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="client-search" />
            </div>
            <div className="relative w-full sm:w-64">
              <Package size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Produit facturé contenant…" value={productQuery} onChange={e => setProductQuery(e.target.value)} className="pl-9" data-testid="client-invoiced-product-search" />
              {productQuery.trim().length > 0 && productQuery.trim().length < PRODUCT_MIN_CHARS && (
                <span className="absolute -bottom-4 left-1 text-[10px] text-slate-400" data-testid="client-invoiced-product-hint">{PRODUCT_MIN_CHARS} caractères minimum</span>
              )}
            </div>
            <Select value={typeFilter} onValueChange={v => setTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-40" data-testid="client-type-filter"><SelectValue placeholder="Tous les types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="client">Clients</SelectItem>
                <SelectItem value="prospect">Prospects</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={statusFilter === "archived" ? "default" : "outline"}
              size="sm"
              className={`text-xs ${statusFilter === "archived" ? "bg-orange-600 hover:bg-orange-700 text-white" : "text-orange-600 border-orange-200 hover:bg-orange-50"}`}
              onClick={() => setStatusFilter(prev => prev === "archived" ? "" : "archived")}
              data-testid="client-archived-filter"
            >
              <Archive size={14} className="mr-1.5" />Archives{statusFilter === "archived" ? " (actif)" : ""}
            </Button>
          </div>
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="add-client-btn">
            <Plus size={16} className="mr-2" /> Nouveau client
          </Button>
          <Button variant="outline" onClick={loadDupes} className="text-xs" data-testid="dedup-btn">
            <Merge size={14} className="mr-1.5" /> Dédoublonner
          </Button>
          {(user?.modules || []).includes("export") && (
            <Button variant="outline" onClick={() => {
              const params = {};
              if (typeFilter) params.type = typeFilter;
              if (search) params.search = search;
              if (statusFilter) params.status = statusFilter;
              window.open(exportAPI.clientsUrl(params), "_blank");
              toast.success("Export CSV clients lance");
            }} className="text-xs" data-testid="export-clients-btn">
              <Download size={14} className="mr-1.5" /> Exporter
            </Button>
          )}
        </div>
        <div className="mb-4 flex items-center justify-between">
          <BulkActionBar count={selection.count} actions={clientBulkActions} onAction={handleBulkAction} disabled={bulkLoading} />
          <span className="text-xs text-slate-400">{total} contact(s)</span>
        </div>
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 px-1 gap-2">
            <span className="text-xs text-slate-500">Page {currentPage}/{totalPages}</span>
            <div className="flex gap-1 flex-wrap">
              <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={currentPage <= 1} onClick={() => setPage(p => p - 1)} data-testid="client-prev-page">Precedent</Button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum;
                if (totalPages <= 7) pageNum = i + 1;
                else if (currentPage <= 4) pageNum = i + 1;
                else if (currentPage >= totalPages - 3) pageNum = totalPages - 6 + i;
                else pageNum = currentPage - 3 + i;
                return (
                  <Button key={pageNum} variant={pageNum === currentPage ? "default" : "outline"} size="sm" className={`h-7 w-7 text-xs ${pageNum === currentPage ? "bg-slate-900 text-white" : ""}`} onClick={() => setPage(pageNum)} data-testid={`client-page-${pageNum}`}>
                    {pageNum}
                  </Button>
                );
              })}
              <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={currentPage >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="client-next-page">Suivant</Button>
            </div>
          </div>
        )}
        <div className="responsive-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10 hidden md:table-cell"><Checkbox checked={selection.isAllSelected} onCheckedChange={selection.toggleAll} data-testid="client-select-all" /></TableHead>
                <TableHead className="uppercase text-xs text-slate-500 hidden lg:table-cell cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("type")} data-testid="sort-type">
                  <span className="flex items-center">Type<SortIcon field="type" /></span>
                </TableHead>
                <TableHead className="uppercase text-xs text-slate-500 cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("company")} data-testid="sort-company">
                  <span className="flex items-center">Société<SortIcon field="company" /></span>
                </TableHead>
                <TableHead className="uppercase text-xs text-slate-500 hidden xl:table-cell cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("nom_etablissement")} data-testid="sort-nom-etablissement">
                  <span className="flex items-center">Etablissement<SortIcon field="nom_etablissement" /></span>
                </TableHead>
                <TableHead className="uppercase text-xs text-slate-500 hidden lg:table-cell cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("last_name")} data-testid="sort-last-name">
                  <span className="flex items-center">Contact<SortIcon field="last_name" /></span>
                </TableHead>
                <TableHead className="uppercase text-xs text-slate-500 hidden xl:table-cell cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("email")} data-testid="sort-email">
                  <span className="flex items-center">Email<SortIcon field="email" /></span>
                </TableHead>
                <TableHead className="uppercase text-xs text-slate-500 hidden xl:table-cell cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("phone")} data-testid="sort-phone">
                  <span className="flex items-center">Téléphone<SortIcon field="phone" /></span>
                </TableHead>
                <TableHead className="uppercase text-xs text-slate-500 hidden lg:table-cell cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("city")} data-testid="sort-city">
                  <span className="flex items-center">Ville<SortIcon field="city" /></span>
                </TableHead>
                <TableHead className="uppercase text-xs text-slate-500">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map(c => (
                <TableRow key={c.id} className={`table-row-hover cursor-pointer ${selection.selected.has(c.id) ? "bg-indigo-50/50" : ""}`} onClick={() => navigate(`/clients/${c.id}`)}>
                  <TableCell className="hidden md:table-cell" onClick={e => e.stopPropagation()}><Checkbox checked={selection.selected.has(c.id)} onCheckedChange={() => selection.toggle(c.id)} data-testid={`client-select-${c.id}`} /></TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Badge variant={c.type === "client" ? "default" : "secondary"} className={c.type === "client" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                      {c.type === "client" ? "Client" : "Prospect"}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium text-xs sm:text-sm">
                    <InvoicedProductMatch client={c}>{c.trade_name || c.company || "-"}</InvoicedProductMatch>
                    {c.trade_name && c.company && <div className="text-[10px] text-slate-400 font-normal truncate max-w-[200px]">{c.company}</div>}
                    <div className="lg:hidden text-[10px] text-slate-400 font-normal">{c.first_name} {c.last_name}</div>
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm hidden xl:table-cell">{c.nom_etablissement || "-"}</TableCell>
                  <TableCell className="hidden lg:table-cell">{c.first_name} {c.last_name}</TableCell>
                  <TableCell className="text-slate-500 hidden xl:table-cell">{c.emails?.length > 0 ? c.emails.join(", ") : (c.email || "-")}</TableCell>
                  <TableCell className="text-slate-500 hidden xl:table-cell">{c.phones?.length > 0 ? c.phones.join(", ") : (c.phone || "-")}</TableCell>
                  <TableCell className="text-slate-500 hidden lg:table-cell">{c.city || "-"}</TableCell>
                  <TableCell>
                    <div className="hidden md:flex gap-1" onClick={e => e.stopPropagation()}>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/clients/${c.id}`)} className="h-7 px-2 text-xs" data-testid={`view-client-${c.id}`}><Eye size={12} className="mr-1" />Voir</Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}><Pencil size={13} /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => remove(c.id)} data-testid={`delete-client-${c.id}`}><Trash2 size={13} /></Button>
                    </div>
                    <div className="md:hidden" onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal size={16} /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[140px]">
                          <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)}><Eye size={12} className="mr-2" />Voir</DropdownMenuItem>
                          <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => openEdit(c)}><Pencil size={12} className="mr-2" />Modifier</DropdownMenuItem>
                          <DropdownMenuItem className="text-xs cursor-pointer text-red-600" onClick={() => remove(c.id)}><Trash2 size={12} className="mr-2" />Supprimer</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {clients.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Aucun client</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-4 px-1 gap-2">
            <span className="text-xs text-slate-500">Page {currentPage}/{totalPages}</span>
            <div className="flex gap-1 flex-wrap">
              <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={currentPage <= 1} onClick={() => setPage(p => p - 1)}>Precedent</Button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum;
                if (totalPages <= 7) pageNum = i + 1;
                else if (currentPage <= 4) pageNum = i + 1;
                else if (currentPage >= totalPages - 3) pageNum = totalPages - 6 + i;
                else pageNum = currentPage - 3 + i;
                return (
                  <Button key={pageNum} variant={pageNum === currentPage ? "default" : "outline"} size="sm" className={`h-7 w-7 text-xs ${pageNum === currentPage ? "bg-slate-900 text-white" : ""}`} onClick={() => setPage(pageNum)}>
                    {pageNum}
                  </Button>
                );
              })}
              <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={currentPage >= totalPages} onClick={() => setPage(p => p + 1)}>Suivant</Button>
            </div>
          </div>
        )}
      </Card>
      <ClientFormDialog open={showDialog} onOpenChange={setShowDialog} editing={editing} form={form} setField={f} onSave={save} />
      <DuplicatesDialog open={showDupes} onOpenChange={setShowDupes} dupes={dupes} merging={merging} onMergeGroup={mergeGroup} onMergeAll={mergeAll} />
    </div>
  );
}
